import React, { useState, useEffect, useRef } from 'react';
import { ListingTable } from "components/listing";
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess } from 'components/user-access';
import { Wizard } from "components/wizard";
import { useValidation } from "hooks/useValidation";
import { useDirtyData } from "hooks/useDirtyData";
import { GridLayout } from 'components/grid/GridLayout';
import { FormMultiSelect, FormSelect, FormNumericInput, FormTextInput, FormDateInput, FormSwitch, FormLocationSelector } from "components/form-fields";
import { FrequencyInput } from "pages/licence-checking/policies/shared"
import PropTypes from 'prop-types';
import { Button } from 'components/buttons';
import { Tooltip } from "components/tooltip";
import { Icon, Position, Intent } from "@blueprintjs/core";
import { LicenceCheckingService, UserService, LocationService } from 'services';
import { NotificationToaster, AlertUnsavedData, AlertConfirm } from 'components/notifications';
import { Numeric } from "components/formatting";
import { useNavigate } from 'react-router-dom';
import { ShowHide } from 'components/layout';
import classNames from 'classnames';
import moment from "moment";
import './LicenceCheckingPolicyWizard.css';

export function LicenceCheckingPolicyWizard(
    {
        loading = false,
        policy = null,
        createPolicy = true,
        credits,
        disabled,
        employeeOptions,
        licenceCheckingOptions
    }
) {
    const requiredAction = "LicenceChecking:Manage";
    const [isValid, errors, validate] = useValidation(false);
    const [isValidPage2, errorsPage2, validatePage2] = useValidation(false);
    const [isValidAdditionalRules, setIsValidAdditionalRules] = useState(false);
    const [canProceed, setCanProceed] = useState(false);
    const [wizardStep, setWizardStep] = useState(0);
    const [policyDetailsText, setPolicyDetailsText] = useState({});
    const [loadingValue, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [wizardPolicy, setWizardPolicy] = useState(null);
    const [additionalRulesForPolicy, setAdditionalRulesForPolicy] = useState([]);
    const [additionalRulesData, setAdditionalRulesData] = useState([]);
    const [creditEstimate, setCreditEstimate] = useState({});
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

    const policyListingUrl = "/licence-checking/policies";

    var navigate = useNavigate();
    const inputRef = useRef(null);

    const isDataDirty = useDirtyData(policy, wizardPolicy);

    const additionalRulesDefault = {
        rule: null,
        threshold: null,
        checkFrequency: null,
        checkFrequencyType: null
    };

    const additionalRulesHeaders = ["Rule", "Threshold", "Check Frequency", "Action"]

    const wizardStepInfo = [{ stepStatus: isValid ? "complete" : "", title: "Details" }, { stepStatus: isValidPage2 ? "complete" : "", "title": "Summary" }];

    //Location
    const [useHierarchy, setUseHierarchy] = useState(true);
    const [locationLookup, setLocationLookup] = useState({ nameById: {}, total: 0 });

    useEffect(() => {
        LocationService.getLocationBusinessAreas().then((businessAreas) => {
            const tachoArea = businessAreas ? businessAreas.find((b) => b.businessArea === "Tacho") : null;
            if (!tachoArea || !tachoArea.locationBusinessAreaId) {
                return null;
            }
            return UserService.getLoggedInUserLocations(tachoArea.locationBusinessAreaId, false, true).then((locHierarchyList) => {
                const locationGroups = locHierarchyList && locHierarchyList.location ? locHierarchyList.location : [];
                const nameById = {};
                let total = 0;
                locationGroups.forEach((group) => {
                    const items = group && group.items ? group.items : [];
                    items.forEach((item) => {
                        if (nameById[item.id] == null) {
                            nameById[item.id] = item.name;
                            total++;
                        }
                    });
                });
                setLocationLookup({ nameById: nameById, total: total });
            });
        }).catch(() => {
            setLocationLookup({ nameById: {}, total: 0 });
        });
    }, []);

    useEffect(() => {
        if (employeeOptions?.employeeCategories?.length > 0 && licenceCheckingOptions?.policyRules?.length > 0
            && policy && (policy.policyName || policy.policyName === "")) {
            setWizardPolicy(policy);
            setAdditionalRulesForPolicy(policy.advancedRules);
        }
    }, [employeeOptions, licenceCheckingOptions, policy]);

    useEffect(() => {
        if (wizardPolicy) {
            validateModel();
        }
    }, [wizardPolicy]);

    useEffect(() => {
        if (additionalRulesForPolicy && wizardPolicy && licenceCheckingOptions?.policyRules?.length > 0) {
            setupAdditionalRulesData();
        }
        if (isValid && isValidAdditionalRules) {
            setupPolicyDetailsText();
        }
        setupCanProceede();
    }, [additionalRulesForPolicy, wizardPolicy, isValid, isValidAdditionalRules]);

    useEffect(() => {
        setupCanProceede();
    }, [isValidPage2, wizardStep, loadingValue, loading, saving]);

    useEffect(() => {
        if (wizardStep === 1 && !loadingValue) {
            setTimeout(function () {
                if (inputRef.current != null) {
                    inputRef.current.focus();
                }
            });

        }
    }, [wizardStep, loadingValue]);

    function setupCanProceede() {
        if (!loadingValue && !loading && !saving &&
            ((wizardStep === 0 && isValid && isValidAdditionalRules) ||
                (wizardStep === 1 && isValid && isValidAdditionalRules && isValidPage2))) {
            setCanProceed(true);
        } else {
            setCanProceed(false);
        }
    }

    function getFrequencyText(checkFrequency, checkFrequencyType, textInline) {
        if (checkFrequency && checkFrequencyType) {
            return <>{textInline ? "e" : "E"}very {checkFrequency} {checkFrequency !== 1 ? checkFrequencyType.name.toLowerCase() : checkFrequencyType.name.toLowerCase().replace("s", "")}</>;
        }
        return <></>;
    }

    function setupPolicyDetailsText() {

        setPolicyDetailsText({
            basePolicyRules:
                wizardPolicy.employeeTypes.map(e => { return e.name }).concat(
                    wizardPolicy.employeeCategories.map(e => { return e.name }).concat(
                        wizardPolicy.employeeStatuses.map(e => { return e.name })
                    )
                ).toString().replaceAll(",", ", "),
            baseFrequency: getFrequencyText(wizardPolicy.checkFrequency, wizardPolicy.checkFrequencyType, false),
            additionalRules: additionalRulesForPolicy.filter(a => a.checkFrequencyType !== null)
                .sort(function (a, b) { return (b["checkFrequencyType"]["daysBetweenChecks"] * b["checkFrequency"]) - (a["checkFrequencyType"]["daysBetweenChecks"] * a["checkFrequency"]) })
                .map(r => { return <>Employees with {r.threshold} or more points (endorsements) on licence {getFrequencyText(r.checkFrequency, r.checkFrequencyType, true)}. <br /></> })
        });
    }

    function getLocationsSummaryText() {
        const max_locations_to_list = 5;
        const selected = wizardPolicy?.employeeLocations ? wizardPolicy.employeeLocations : [];
        if (locationLookup.total > 0 && selected.length === locationLookup.total) {
            return "All locations";
        }
        if (selected.length <= max_locations_to_list) {
            const names = selected.map((id) => locationLookup.nameById[id]);
            if (names.every((name) => name != null)) {
                return names.join(", ");
            }
        }
        return `${selected.length} locations`;
    }

    function validateAdditionalRules() {
        let thresholdDaysDiffList = [];
        let additionalRuleErrors = [];
        let errorCount = 0;
        let baseDaysDiff = wizardPolicy.checkFrequencyType.daysBetweenChecks * wizardPolicy.checkFrequency;

        additionalRulesForPolicy.forEach(r => {
            let errorMsg = "";

            if (!r.threshold || !r.checkFrequency || !r.checkFrequencyType) {
                errorMsg = "All values must be entered."
                errorCount++;
            } else {
                let thresholdDaysDiff = { threshold: r.threshold, days: r.checkFrequencyType.daysBetweenChecks * r.checkFrequency };

                if (thresholdDaysDiff.days >= baseDaysDiff) {
                    errorMsg = "Additional rules licence checks must occur more often than the base frequency."
                    errorCount++;
                } else if (thresholdDaysDiffList.some(d => d.threshold === thresholdDaysDiff.threshold ||
                    (d.threshold > thresholdDaysDiff.threshold && d.days >= thresholdDaysDiff.days) ||
                    (d.threshold < thresholdDaysDiff.threshold && d.days <= thresholdDaysDiff.days))) {
                    errorMsg = "Thesholds must be unique and as they increase the licence checks must occur more often."
                    errorCount++;
                }

                thresholdDaysDiffList.push(thresholdDaysDiff);
            }

            additionalRuleErrors.push(errorMsg);
        });

        setIsValidAdditionalRules(errorCount === 0);
        return additionalRuleErrors;
    }

    function setupAdditionalRulesData() {
        var additionalRuleErrors = validateAdditionalRules();
        var ar = additionalRulesForPolicy.map((r, index) => {
            let errorIcon = <></>;

            if (additionalRuleErrors[index] && additionalRuleErrors[index].length > 0) {
                errorIcon =
                    <Tooltip content={additionalRuleErrors[index]} position={Position.TOP} >
                        <Icon className="text-intent-danger" icon="warning-sign" />
                    </Tooltip>;
            }

            return (
                [<div className="inline-items policy-rule-select-row" key={`policy-rule-${r.rule.id}-${index}`}>
                    <FormSelect
                        items={licenceCheckingOptions.policyRules}
                        selectedValue={r.rule.id}
                        onItemSelect={(item) => { onAdditionalRulesEditChange(index, item, "rule") }}
                        placeholder="Please select to create a new rule"
                        small
                    ></FormSelect>
                </div>,
                <div className="inline-items" key={`policy-rule-threshold-${r.rule.id}-${index}`}>
                    <FormNumericInput
                        min={1}
                        max={12}
                        selectedValue={r.threshold}
                        onValueChange={(item) => { onAdditionalRulesEditChange(index, item, "threshold") }}
                    ></FormNumericInput>
                    <div>
                        Points or more
                    </div>
                </div>,
                <div className="inline-items" key={`policy-rule-frequency-${r.rule.id}-${index}`}>
                    <div>Check every</div>
                    <FrequencyInput
                        frequencySelectedValue={r.checkFrequency}
                        frequencyTypeSelectedValue={r.checkFrequencyType?.id ? r.checkFrequencyType?.id : r.checkFrequencyType}
                        onFrequencyChange={(item) => { onAdditionalRulesEditChange(index, item, "checkFrequency") }}
                        min={1}
                        max={1000}
                        onFrequencyTypeChange={(item) => { onAdditionalRulesEditChange(index, item, "checkFrequencyType") }}
                    />
                    {errorIcon}
                </div>,
                <div className="inline-items" key={`policy-rule-icon-${r.rule.id}-${index}`}>
                    <Button iconOnly icon="trash" minimal large={false} onClick={() => deleteAdditionRule(index)} />
                </div>
                ]
            )
        });

        //Add the dummy row to apply a new additional rule
        let blankRow = (
            [
                <div className="inline-items" key="policy-rule-blank">
                    <div className='inline-item  policy-rule-select-row'>
                        <FormSelect
                            items={licenceCheckingOptions.policyRules}
                            selectedValue={null}
                            onItemSelect={onAdditionalRulesAddRuleSelect}
                            placeholder="Please select to create a new rule"
                            small
                        ></FormSelect>
                    </div>
                </div>,
                <div className="inline-items" key="policy-rule-threshold-blank" >
                    <FormNumericInput
                        disabled
                        disabledInput
                        onValueChange={() => { }}
                    ></FormNumericInput>
                    <div>
                        Points or more
                    </div>
                </div>,
                <div className="inline-items" key="policy-rule-frequency-blank">
                    <div>Check every</div>
                    <FrequencyInput
                        disabled
                        onFrequencyChange={() => { }}
                        onFrequencyTypeChange={() => { }}
                    />
                </div>,
                <></>
            ]);
        ar = [...ar, blankRow];

        setAdditionalRulesData(ar);
    }

    function onAdditionalRulesAddRuleSelect(item) {
        setAdditionalRulesForPolicy((prev) => {
            let newPolicyRule = additionalRulesDefault;
            newPolicyRule.rule = item;
            return [...prev, newPolicyRule];
        });
    }

    function deleteAdditionRule(index) {
        setAdditionalRulesForPolicy((prev) => {
            var newInstance = [...prev];
            newInstance.splice(index, 1);
            return newInstance;
        });
    }

    function onAdditionalRulesEditChange(index, item, name) {
        setAdditionalRulesForPolicy((prev) => {
            var newInstance = [...prev];
            newInstance[index][name] = item;
            return newInstance;
        });
    }

    function validateModel() {
        let rules = [
            { fieldName: "employeeCategories", required: true },
            { fieldName: "employeeTypes", required: true },
            { fieldName: "employeeStatuses", required: true },
            { fieldName: "checkFrequencyType", required: true },
            { fieldName: "filterByLocations", required: true }
        ];
        let externalRules = [];

        if (wizardPolicy.filterByLocations && (wizardPolicy.employeeLocations == null || wizardPolicy.employeeLocations.length === 0)) {
            externalRules.push({
                fieldName: "employeeLocations",
                valid: false,
                errorMessage: "At least one location must be selected if filtering by location."
            });
        }

        validate(rules, wizardPolicy, externalRules);
        validateModelPage2();
    }

    function validateModelPage2() {
        let rules = [
            { fieldName: "policyName", required: true },
            { fieldName: "startDate", required: true }
        ];

        validatePage2(rules, wizardPolicy);
    }

    function onEmployeeMultiSelectChange(item, name) {
        setWizardPolicy((prev) => {
            var newInstance = {
                ...prev
            };
            if (!newInstance[name].some(c => c.id === item.id)) {
                newInstance[name] = [...newInstance[name], item];
            }
            return newInstance;
        });
    }

    function onEmployeeRemove(index, name) {
        setWizardPolicy((prev) => {
            var newInstance = {
                ...prev
            };
            newInstance[name] = newInstance[name].filter(c => c.name !== index)
            return newInstance;
        });
    }

    function onEmployeeChange(item, name) {
        setWizardPolicy((prev) => {
            var newInstance = {
                ...prev
            };
            newInstance[name] = item;
            return newInstance;
        });
    }

    function onFilterByLocationsChange(e) {
        setWizardPolicy((prev) => {
            var newInstance = {
                ...prev
            };
            newInstance["filterByLocations"] = e.target.checked;
            if (!e.target.checked) {
                newInstance["employeeLocations"] = [];
            }
            return newInstance;
        });
    }

    function onSelectedLocationsChange(selectedLocations) {
        setWizardPolicy((prev) => {
            var newInstance = {
                ...prev
            };
            newInstance["employeeLocations"] = selectedLocations;
            return newInstance;
        });
    }

    function getBaseApiModel() {
        return {
            "employeeCategories": wizardPolicy.employeeCategories.map(e => e.id),
            "employeeTypes": wizardPolicy.employeeTypes.map(e => e.id),
            "employeeStatuses": wizardPolicy.employeeStatuses.map(e => e.id),
            "checkFrequency": wizardPolicy.checkFrequency,
            "checkFrequencyType": wizardPolicy.checkFrequencyType.id,
            "filterByLocations": wizardPolicy.filterByLocations,
            "employeeLocations": wizardPolicy.filterByLocations ? wizardPolicy.employeeLocations : [],
            "advancedRules": additionalRulesForPolicy.map(r => {
                return {
                    "policyRuleId": r.rule.id,
                    "ruleThreshold": r.threshold,
                    "checkFrequency": r.checkFrequency,
                    "checkFrequencyType": r.checkFrequencyType.id
                };
            })
        };
    }

    function getCreditEstimate() {
        var errorHandler = function () {
            NotificationToaster.show(Intent.DANGER, "Failed to obtain credit estimate. Please try again.");
        }

        let model = getBaseApiModel();

        if (!createPolicy) {
            model.policyId = policy.id;
        }

        setLoading(true);
        LicenceCheckingService.getPolicyCreditEstimate(model).then(function (resp) {
            setCreditEstimate(resp);
        }, errorHandler).catch(() => {
            errorHandler()
        }).finally(() => {
            setLoading(false);
        });
    }

    function onWizardFinish() {
        let model = getBaseApiModel();
        model.policyName = wizardPolicy.policyName;
        model.startDate = wizardPolicy.startDate;
        if (!createPolicy) {
            model.active = wizardPolicy.active;
        }

        setSaving(true);

        var errorHandler = function (error) {
            const badRequestCode = 400;
            if (error?.response?.status === badRequestCode) {
                NotificationToaster.show(Intent.WARNING, error.response.data);
            } else {
                NotificationToaster.show(Intent.DANGER, `Failed to ${createPolicy ? "create" : "update"} policy. Please try again.`);
            }
        }

        let funcName = createPolicy ? "createPolicy" : "updatePolicy";

        LicenceCheckingService[funcName](model, policy.id).then(function (resp) {
            NotificationToaster.show(Intent.SUCCESS, `Your policy has been successfully ${createPolicy ? "created" : "updated"}.`);

            //Allow us to navigate away with no dirty data
            setWizardPolicy(policy);
            //Redirect at the end of the render cycle
            setTimeout(() => {
                navigate(policyListingUrl);
            }, 100);
        }, errorHandler).catch(() => {
            errorHandler();
        }).finally(() => {
            setSaving(false);
        });
    }

    function onWizardChange(step) {
        setWizardStep(step);

        if (step === 0) {
            setCanProceed(isValid && isValidAdditionalRules);
        }

        if (step === 1) {
            getCreditEstimate();
        }
    }

    function deletePolicy() {
        setShowDeleteConfirm(true);
    }

    function onDeleteConfirm() {
        setSaving(true);

        var errorHandler = function (error) {
            NotificationToaster.show(Intent.DANGER, "Failed to delete policy listing. Please try again.");
        }

        LicenceCheckingService.deletePolicy(wizardPolicy.id).then(function () {
            NotificationToaster.show(Intent.SUCCESS, "Policy has been successfully deleted.");
            setTimeout(() => navigate(policyListingUrl), 0);
        }, errorHandler).catch(errorHandler)
            .finally(() => {
                setShowDeleteConfirm(false);
                setLoading(false);
            });
    }

    function onDeleteCancel() {
        setShowDeleteConfirm(false);
    }

    function getDeleteButtons() {
        return (
            <Button
                onClick={deletePolicy}
                icon="trash"
                text="Delete"
                loading={loadingValue || loading}
                disabled={saving}
                intent='DANGER'
            />
        );
    }

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <div>
                    <Wizard onStepChange={onWizardChange} onFinish={onWizardFinish} canProceed={canProceed}
                        disabled={saving} finishButtonText={createPolicy ? "Create Policy" : "Update Policy"}
                        additionalButtons={createPolicy ? null : getDeleteButtons()}
                        alignButtonsRight
                        allowDirectStepNavigation={!createPolicy && canProceed}
                        additionalStepInformation={wizardStepInfo}
                    >
                        <div>
                            <h2>Policy Rules</h2>
                            <h3>Employees</h3>
                            <div>
                                <GridLayout numberOfColumns={3} gap={20}>
                                    <FormMultiSelect
                                        loading={loadingValue || loading}
                                        disabled={saving}
                                        items={employeeOptions?.employeeTypes}
                                        onItemSelect={(item) => onEmployeeMultiSelectChange(item, "employeeTypes")}
                                        onRemove={(item) => onEmployeeRemove(item, "employeeTypes")}
                                        placeholder="Please select"
                                        headingText="Employee Type"
                                        dangerHelperText={errors.employeeTypes}
                                        selectedValues={wizardPolicy?.employeeTypes ? wizardPolicy?.employeeTypes : []}>
                                    </FormMultiSelect>
                                    <FormMultiSelect
                                        loading={loadingValue || loading}
                                        disabled={saving}
                                        items={employeeOptions?.employeeCategories}
                                        onItemSelect={(item) => onEmployeeMultiSelectChange(item, "employeeCategories")}
                                        onRemove={(item) => onEmployeeRemove(item, "employeeCategories")}
                                        placeholder="Please select"
                                        headingText="Employee Category"
                                        dangerHelperText={errors.employeeCategories}
                                        selectedValues={wizardPolicy?.employeeCategories ? wizardPolicy?.employeeCategories : []}>
                                    </FormMultiSelect>
                                    <FormMultiSelect
                                        loading={loadingValue || loading}
                                        disabled={saving}
                                        items={employeeOptions?.employeeStatuses}
                                        onItemSelect={(item) => onEmployeeMultiSelectChange(item, "employeeStatuses")}
                                        onRemove={(item) => onEmployeeRemove(item, "employeeStatuses")}
                                        placeholder="Please select"
                                        headingText="Employee Status"
                                        dangerHelperText={errors.employeeStatuses}
                                        selectedValues={wizardPolicy?.employeeStatuses ? wizardPolicy?.employeeStatuses : []}>
                                    </FormMultiSelect>
                                    <FormSwitch
                                        headingText="Filter by locations"
                                        checked={wizardPolicy?.filterByLocations}
                                        onChange={onFilterByLocationsChange}
                                        loading={loadingValue || loading}
                                        dangerHelperText={errors.filterByLocations}
                                    />
                                    <ShowHide
                                        evaluator={wizardPolicy?.filterByLocations}
                                        show={
                                            <FormLocationSelector
                                                headingText='Locations'
                                                businessArea={"Tacho"}
                                                loading={loadingValue || loading}
                                                selectedLocations={wizardPolicy?.employeeLocations ? wizardPolicy?.employeeLocations : []}
                                                setSelectedLocations={onSelectedLocationsChange}
                                                useLocationDefaults={false}
                                                useHierarchy={useHierarchy}
                                                setUseHierarchy={setUseHierarchy}
                                                locationDefaultsToTrue={false}
                                                dangerHelperText={errors.employeeLocations}
                                            />
                                        }
                                    />

                                </GridLayout>
                            </div>
                            <h3>Base Check Frequency</h3>
                            <div className="spacer-bottom-medium">Check every</div>
                            <div className="spacer-bottom">
                                <FrequencyInput
                                    loading={loadingValue || loading}
                                    disabled={disabled}
                                    frequencySelectedValue={wizardPolicy?.checkFrequency}
                                    onFrequencyChange={(item) => onEmployeeChange(item, "checkFrequency")}
                                    min={1}
                                    max={1000}
                                    frequencyTypeSelectedValue={wizardPolicy?.checkFrequencyType?.id ? wizardPolicy?.checkFrequencyType?.id : wizardPolicy?.checkFrequencyType}
                                    onFrequencyTypeChange={(item) => onEmployeeChange(item, "checkFrequencyType")}
                                    frequencyTypeDangerHelperText={errors.checkFrequencyType}
                                    className="align-items-flex-start"
                                />
                            </div>
                            <h3>Additional Rules</h3>
                            <div className="spacer-bottom">
                                <ListingTable
                                    id="policy-listing"
                                    headers={additionalRulesHeaders}
                                    data={additionalRulesData}
                                    totalRecordCount={additionalRulesData.length}
                                    pageable={false}
                                    loadingData={loadingValue || loading}
                                    columnWidths={[390, 290, 480, 80]}
                                />
                            </div>
                        </div>

                        <div>
                            <h2>Policy Details</h2>
                            <div className="spacer-bottom">
                                <GridLayout customColumnRatios={wizardPolicy?.filterByLocations && wizardPolicy?.employeeLocations?.length > 0 ? [2, 1, 1, 1] : [2, 1, 1]} gap={20}>
                                    <div className="spacer-bottom">
                                        <h4 className="bold">Base Policy Rules</h4>
                                        <div>{policyDetailsText.basePolicyRules}</div>
                                    </div>
                                    <ShowHide
                                        evaluator={wizardPolicy?.filterByLocations && wizardPolicy?.employeeLocations?.length > 0}
                                        show={(
                                            <div>
                                                <h4 className="bold">Locations</h4>
                                                <div>{getLocationsSummaryText()}</div>
                                            </div>
                                        )}
                                    />
                                    <div>
                                        <h4 className="bold">Base Frequency</h4>
                                        <div>{policyDetailsText.baseFrequency}</div>
                                    </div>
                                    <div>
                                        <h4 className="bold">Employees Covered</h4>
                                        <div className={classNames({ "bp6-skeleton": loadingValue || loading })}>
                                            {<Numeric>{creditEstimate.employeeCount}</Numeric>} ({<Numeric>{creditEstimate.enrolledEmployeeCount}</Numeric>} currently enrolled)
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="bold">Additional Rules and Frequency</h4>
                                        <div>{policyDetailsText.additionalRules == null || policyDetailsText.additionalRules.length === 0 ? <em>None</em> : policyDetailsText.additionalRules}</div>
                                    </div>
                                    <div>
                                        <h4 className="bold">Yearly Credit Estimate</h4>
                                        <div className={classNames({ "bp6-skeleton": loadingValue || loading })}>
                                            {<Numeric>{creditEstimate.creditEstimate}</Numeric>} ({<Numeric>{creditEstimate.enrolledCreditEstimate}</Numeric>} based on enrollment)
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="bold">Current Credit Balance</h4>
                                        <div className={classNames({ "bp6-skeleton": loadingValue || loading })}>
                                            {<Numeric>{credits}</Numeric>}
                                        </div>
                                    </div>
                                </GridLayout>
                            </div>
                            <FormTextInput
                                headingText="Policy name"
                                loading={loadingValue || loading}
                                disabled={saving}
                                onChange={(event) => onEmployeeChange(event.target.value, "policyName")}
                                value={wizardPolicy?.policyName}
                                maxLength={255}
                                inputRef={inputRef}
                                dangerHelperText={errorsPage2.policyName}
                            />
                            <ShowHide
                                evaluator={createPolicy}
                                show={(
                                    <FormDateInput
                                        loading={loadingValue || loading}
                                        disabled={saving}
                                        headingText="Policy start date"
                                        onChange={(item) => onEmployeeChange(moment(item).format("YYYY-MM-DD"), "startDate")}
                                        value={wizardPolicy?.startDate}
                                        minDate={moment().startOf('day').toDate()}
                                        dangerHelperText={errorsPage2.startDate}
                                    ></FormDateInput>
                                )}
                                hide={(
                                    <>
                                        <FormDateInput
                                            disabled={true}
                                            headingText="Policy start date"
                                            onChange={() => { }}
                                            value={wizardPolicy?.startDate}
                                        ></FormDateInput>
                                        <FormSwitch
                                            headingText="Policy active"
                                            loading={loadingValue || loading}
                                            disabled={saving}
                                            checked={wizardPolicy?.active}
                                            onChange={(item) => onEmployeeChange(item.target.checked, "active")}
                                        />
                                    </>
                                )}
                            ></ShowHide>
                        </div>
                    </Wizard>
                    <AlertUnsavedData
                        isDirty={isDataDirty}>
                    </AlertUnsavedData>
                    <AlertConfirm
                        title="Please confirm"
                        isOpen={showDeleteConfirm}
                        onConfirm={onDeleteConfirm}
                        onCancel={onDeleteCancel}
                        saving={saving}
                    >
                        <p>Are you sure you want to delete this policy?</p>
                    </AlertConfirm>
                </div>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

LicenceCheckingPolicyWizard.propTypes = {
    loading: PropTypes.bool,
    policy: PropTypes.object,
    employeeOptions: PropTypes.object.isRequired,
    licenceCheckingOptions: PropTypes.object.isRequired,
    credits: PropTypes.number.isRequired,
    createPolicy: PropTypes.bool
};